import { useState } from 'react';
import { reviews, Review } from '@/mocks/reviews';
import GlowButton from '@/components/base/GlowButton';

interface CommunityReviewsProps {
  communityId: string;
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const sz = size === 'lg' ? 'text-lg' : 'text-sm';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={`${sz} ${s <= rating ? 'text-amber-400' : 'text-slate-200'}`}>
          <i className={s <= rating ? 'ri-star-fill' : 'ri-star-line'}></i>
        </span>
      ))}
    </div>
  );
}

function InteractiveStars({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(s)}
          className="text-2xl cursor-pointer transition-transform hover:scale-110"
        >
          <i className={`${s <= (hovered || value) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-slate-300'}`}></i>
        </button>
      ))}
    </div>
  );
}

function RatingBar({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-4 shrink-0">{label}</span>
      <i className="ri-star-fill text-amber-400 text-xs shrink-0"></i>
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-400 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        ></div>
      </div>
      <span className="text-xs text-slate-400 w-4 text-right shrink-0">{count}</span>
    </div>
  );
}

export default function CommunityReviews({ communityId }: CommunityReviewsProps) {
  const communityReviews = reviews.filter((r) => r.communityId === communityId);
  const [localReviews, setLocalReviews] = useState<Review[]>(communityReviews);
  const [showForm, setShowForm] = useState(false);
  const [newRating, setNewRating] = useState(0);
  const [newContent, setNewContent] = useState('');
  const [newAuthor, setNewAuthor] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [helpfulMap, setHelpfulMap] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sortBy, setSortBy] = useState<'helpful' | 'recent'>('helpful');

  const avgRating = localReviews.length > 0
    ? localReviews.reduce((s, r) => s + r.rating, 0) / localReviews.length
    : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: localReviews.filter((r) => r.rating === star).length,
  }));

  const sorted = [...localReviews].sort((a, b) =>
    sortBy === 'helpful' ? b.helpful - a.helpful : new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const handleContentChange = (v: string) => {
    if (v.length <= 500) {
      setNewContent(v);
      setCharCount(v.length);
    }
  };

  const handleSubmit = () => {
    if (!newRating || !newContent.trim() || !newAuthor.trim()) return;
    const review: Review = {
      id: `local-${Date.now()}`,
      communityId,
      author: newAuthor.trim(),
      avatar: `https://readdy.ai/api/search-image?query=professional%20person%20portrait%20headshot%20clean%20white%20background%20minimal%20style&width=48&height=48&seq=${Date.now()}&orientation=squarish`,
      rating: newRating,
      date: new Date().toISOString().split('T')[0],
      content: newContent.trim(),
      helpful: 0,
      verified: false,
    };
    setLocalReviews((prev) => [review, ...prev]);
    setNewRating(0);
    setNewContent('');
    setNewAuthor('');
    setCharCount(0);
    setShowForm(false);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const toggleHelpful = (id: string) => {
    setHelpfulMap((prev) => {
      const already = prev[id];
      setLocalReviews((rs) =>
        rs.map((r) => r.id === id ? { ...r, helpful: r.helpful + (already ? -1 : 1) } : r)
      );
      return { ...prev, [id]: !already };
    });
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white/80 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center text-[#0088CC]">
            <i className="ri-star-line text-base"></i>
          </span>
          Community Reviews
        </h2>
        <GlowButton
          onClick={() => setShowForm(!showForm)}
          className="text-sm font-semibold px-3 py-1.5 rounded-lg"
        >
          <i className="ri-edit-line text-sm w-4 h-4 flex items-center justify-center"></i>
          Write a Review
        </GlowButton>
      </div>

      {/* Success toast */}
      {submitted && (
        <div className="flex items-center gap-2 bg-emerald-50/70 backdrop-blur-sm border border-emerald-200/60 text-emerald-700 text-sm px-4 py-2.5 rounded-xl mb-4">
          <i className="ri-check-double-line w-4 h-4 flex items-center justify-center"></i>
          Your review has been posted. Thank you!
        </div>
      )}

      {/* Rating Summary */}
      <div className="flex flex-col sm:flex-row gap-6 mb-6 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-[#c5d9eb]/40">
        {/* Big score */}
        <div className="flex flex-col items-center justify-center shrink-0 min-w-[100px]">
          <div className="text-5xl font-extrabold text-slate-800 leading-none">
            {localReviews.length > 0 ? avgRating.toFixed(1) : '—'}
          </div>
          <StarRating rating={Math.round(avgRating)} size="lg" />
          <p className="text-xs text-slate-400 mt-1">{localReviews.length} review{localReviews.length !== 1 ? 's' : ''}</p>
        </div>
        {/* Bars */}
        <div className="flex-1 flex flex-col gap-1.5 justify-center">
          {ratingCounts.map(({ star, count }) => (
            <RatingBar key={star} label={String(star)} count={count} total={localReviews.length} />
          ))}
        </div>
      </div>

      {/* Write Review Form */}
      {showForm && (
        <div className="mb-6 bg-[#0088CC]/5 border border-[#0088CC]/15 rounded-xl p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-3">Share Your Experience</h3>
          <div className="mb-3">
            <p className="text-xs text-slate-500 mb-1.5">Your rating</p>
            <InteractiveStars value={newRating} onChange={setNewRating} />
          </div>
          <div className="mb-3">
            <input
              type="text"
              placeholder="Your name"
              value={newAuthor}
              onChange={(e) => setNewAuthor(e.target.value)}
              className="w-full bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl px-4 py-2.5 text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-[#0088CC]/40 focus:bg-white/70 transition-colors"
            />
          </div>
          <div className="mb-1">
            <textarea
              placeholder="Tell others about your experience with this community..."
              value={newContent}
              onChange={(e) => handleContentChange(e.target.value)}
              rows={3}
              className="w-full bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl px-4 py-2.5 text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-[#0088CC]/40 focus:bg-white/70 resize-none transition-colors"
            />
          </div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-slate-400">Be honest and helpful</span>
            <span className={`text-[11px] ${charCount >= 450 ? 'text-red-400' : 'text-slate-400'}`}>{charCount}/500</span>
          </div>
          <div className="flex gap-2">
            <GlowButton
              onClick={handleSubmit}
              disabled={!newRating || !newContent.trim() || !newAuthor.trim()}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
            >
              Post Review
            </GlowButton>
            <GlowButton
              onClick={() => setShowForm(false)}
              variant="secondary"
              className="px-4 py-2.5 rounded-xl text-sm whitespace-nowrap"
            >
              Cancel
            </GlowButton>
          </div>
        </div>
      )}

      {/* Sort */}
      {localReviews.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-slate-400">Sort by:</span>
          {(['helpful', 'recent'] as const).map((opt) => (
            <GlowButton
              key={opt}
              onClick={() => setSortBy(opt)}
              className="text-xs px-3 py-1 rounded-full"
              variant={sortBy === opt ? 'primary' : 'secondary'}
            >
              {opt === 'helpful' ? 'Most Helpful' : 'Most Recent'}
            </GlowButton>
          ))}
        </div>
      )}

      {/* Reviews List */}
      {localReviews.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 mb-3">
            <i className="ri-chat-3-line text-slate-400 text-xl"></i>
          </div>
          <p className="text-sm text-slate-500 font-medium mb-1">No reviews yet</p>
          <p className="text-xs text-slate-400">Be the first to share your experience!</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {sorted.map((review) => (
            <div key={review.id} className="bg-white/60 backdrop-blur-sm rounded-xl border border-[#c5d9eb]/40 p-4">
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-white/80">
                  <img src={review.avatar} alt={review.author} className="w-full h-full object-cover object-top" />
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-slate-700">{review.author}</span>
                    {review.verified && (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">
                        <i className="ri-shield-check-line text-[9px]"></i>
                        Verified
                      </span>
                    )}
                    <span className="text-[11px] text-slate-400 ml-auto">{formatDate(review.date)}</span>
                  </div>
                  <StarRating rating={review.rating} />
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">{review.content}</p>
                  {/* Helpful */}
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => toggleHelpful(review.id)}
                      className={`flex items-center gap-1.5 text-xs transition-all cursor-pointer ${
                        helpfulMap[review.id] ? 'text-[#0088CC]' : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`${helpfulMap[review.id] ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-xs`}></i>
                      </span>
                      Helpful ({review.helpful})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
